import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Globe, User, Mail, Phone, MapPin, Music, Target, Save } from 'lucide-react';
import { API } from '../App';
import { translations, getTranslation } from '../translations';
import { toast } from 'sonner';

const LANGUAGE_OPTIONS = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'hi', name: 'हिंदी (Hindi)', flag: '🇮🇳' },
  { code: 'es', name: 'Español (Spanish)', flag: '🇪🇸' },
  { code: 'ar', name: 'العربية (Arabic)', flag: '🇸🇦' },
  { code: 'fr', name: 'Français (French)', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch (German)', flag: '🇩🇪' }
];

export default function Settings({ user, onLogout }) {
  const navigate = useNavigate();
  const [language, setLanguage] = useState(user?.language || 'en');
  const [saving, setSaving] = useState(false);
  const t = (key) => getTranslation(language, key);

  useEffect(() => {
    if (user?.language && user.language !== language) {
      setLanguage(user.language);
    }
  }, [user?.language]);

  const handleLanguageChange = async (newLanguage) => {
    setLanguage(newLanguage);
    setSaving(true);
    
    try {
      await axios.patch(`${API}/auth/update-profile`, {
        language: newLanguage
      });
      
      toast.success(getTranslation(newLanguage, 'language_updated') || 'Language updated successfully!');
      
      // Force full page reload to apply language across entire app
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('Error updating language:', error);
      toast.error('Failed to update language');
      setLanguage(user?.language || 'en');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-screen flex flex-col pb-20 bg-gradient-to-b from-white via-stone-50 to-stone-100">
      {/* Compact Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium text-sm">{t('back')}</span>
          </button>
          <h1 className="text-lg font-bold text-gray-800">{t('settings')}</h1>
          <div className="w-16" />
        </div>
      </div>

      {/* Main Content - Single screen */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="space-y-4 max-h-[calc(100vh-140px)] overflow-y-auto">
        {/* Language Settings Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Globe className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">{t('language_preference')}</h2>
              <p className="text-sm text-gray-500">{t('select_language_desc')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {LANGUAGE_OPTIONS.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                disabled={saving}
                className={`p-4 rounded-xl border-2 transition-all ${
                  language === lang.code
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{lang.flag}</span>
                  <div className="text-left flex-1">
                    <div className="font-semibold text-gray-800">{lang.name}</div>
                    <div className="text-xs text-gray-500">{lang.code.toUpperCase()}</div>
                  </div>
                  {language === lang.code && (
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          {saving && (
            <div className="mt-4 text-center text-sm text-blue-600 font-medium">
              {t('saving')}...
            </div>
          )}
        </div>

        {/* Profile Information Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">{t('profile_information')}</h2>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <User className="w-5 h-5 text-gray-500" />
              <div className="flex-1">
                <div className="text-xs text-gray-500">{t('name')}</div>
                <div className="font-medium text-gray-800">{user?.name} {user?.surname}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Mail className="w-5 h-5 text-gray-500" />
              <div className="flex-1">
                <div className="text-xs text-gray-500">{t('email')}</div>
                <div className="font-medium text-gray-800">{user?.email}</div>
              </div>
            </div>

            {user?.gender && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <User className="w-5 h-5 text-gray-500" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">{t('gender')}</div>
                  <div className="font-medium text-gray-800 capitalize">{user.gender}</div>
                </div>
              </div>
            )}

            {user?.phone && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Phone className="w-5 h-5 text-gray-500" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">{t('phone_number')}</div>
                  <div className="font-medium text-gray-800">{user.country_code} {user.phone}</div>
                </div>
              </div>
            )}

            {user?.country && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <MapPin className="w-5 h-5 text-gray-500" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">{t('location')}</div>
                  <div className="font-medium text-gray-800">
                    {user.area && `${user.area}, `}{user.city && `${user.city}, `}{user.country}
                  </div>
                </div>
              </div>
            )}

            {user?.music_preferences?.length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Music className="w-5 h-5 text-gray-500" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">{t('music_preferences')}</div>
                  <div className="font-medium text-gray-800">{user.music_preferences.join(', ')}</div>
                </div>
              </div>
            )}

            {user?.wellness_goals?.length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Target className="w-5 h-5 text-gray-500" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">{t('wellness_goals')}</div>
                  <div className="font-medium text-gray-800">{user.wellness_goals.join(', ')}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Logout Button */}
        <div className="mt-6">
          <button
            onClick={onLogout}
            className="w-full py-3 bg-red-500 hover:bg-red-600 text-white font-medium rounded-xl transition-colors"
          >
            {t('logout')}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}
